import React from "react";
import { Plus, Check } from "lucide-react";

import { cn } from "@/lib/style";

import { PlaceCardActions } from "./PlaceCardActions";
import { PlaceCardImage } from "./PlaceCardImage";
import { PlaceCardOverlay, PlaceCardMetadata } from "./PlaceCardOverlay";

export interface BasePlaceCardProps {
  // Core place data
  place: {
    id: string
    name: string
    imageUrl: string
    category?: string
    rating?: number // 0.0-5.0
    priceLevel?: number // 1-4 ($-$$$$)
    distance?: string
  }

  // Variant configuration
  variant: 'compact' | 'expanded' | 'timeline'
  imageAspectRatio: '1:1' | '6:5' | '16:9' | '4:3'

  // Display customization
  showSaveIndicator?: boolean // Red heart badge (used sparingly)
  showChevron?: boolean // Future: for navigation

  // Candidate-specific metadata (optional)
  savedDate?: Date
  savedFrom?: 'search' | 'map' | 'ai' | 'manual'
  userNote?: string // Post-MVP

  // Save functionality (for search results)
  showSaveButton?: boolean // Show save button on card
  isSaved?: boolean // Whether place is already saved
  onSave?: () => void // Save place to candidates

  // Interaction callbacks
  onTap?: () => void // Primary action: show place details
  onRemove?: () => void // Remove from candidates
  onSchedule?: () => void // Quick-add to schedule

  // Style overrides
  className?: string
  style?: React.CSSProperties
}

/**
 * Shared place card component for search results, candidates, and events
 *
 * Features:
 * - Three variants: compact (140px), expanded (360px), timeline
 * - Four aspect ratios: 1:1 (square), 6:5, 16:9, 4:3
 * - Magazine-quality imagery with glossy effect
 * - Monochromatic design with subtle red heart for saved items
 * - Responsive typography and spacing
 *
 * Usage:
 * ```tsx
 * // Search result in grid
 * <BasePlaceCard
 *   variant="compact"
 *   imageAspectRatio="1:1"
 *   place={place}
 *   onTap={() => showDetails(place.id)}
 * />
 *
 * // Saved candidate
 * <BasePlaceCard
 *   variant="compact"
 *   imageAspectRatio="6:5"
 *   place={place}
 *   showSaveIndicator={true}
 *   savedDate={candidate.saved}
 *   onTap={() => showDetails(place.id)}
 *   onRemove={() => remove(place.id)}
 *   onSchedule={() => schedule(place.id)}
 * />
 * ```
 */
export function BasePlaceCard({
  place,
  variant,
  imageAspectRatio,
  showSaveIndicator = false,
  showChevron = false,
  showSaveButton = false,
  isSaved = false,
  onSave,
  savedDate,
  savedFrom,
  userNote,
  onTap,
  onRemove,
  onSchedule,
  className,
  style,
}: BasePlaceCardProps) {
  const isCompact = variant === 'compact'
  const isExpanded = variant === 'expanded'
  const hasActions = onRemove || onSchedule

  // Card dimensions based on variant
  const cardWidth = {
    compact: 'w-[140px]',
    expanded: 'w-full max-w-[360px]',
    timeline: 'w-full',
  }[variant]

  // Card wrapper - button if tappable AND no action buttons AND no save button (to avoid nested buttons)
  // If there are action buttons or save button, use div wrapper with onClick on the image area instead
  const CardWrapper = onTap && !hasActions && !showSaveButton ? 'button' : 'div'
  const cardProps = onTap && !hasActions && !showSaveButton
    ? {
        onClick: onTap,
        type: 'button' as const,
      }
    : {}

  return (
    <CardWrapper
      className={cn(
        // Base card styling - monochromatic
        'relative overflow-hidden bg-magazine-paper',
        'border border-magazine-pearl rounded-card',
        'shadow-card',

        // Card dimensions
        cardWidth,

        // Interactive behavior
        onTap && [
          'cursor-pointer transition-all duration-300 ease-luxury',
          'hover:shadow-card-hover hover:scale-[1.02]',
          'focus:outline-none focus:ring-2 focus:ring-magazine-ash/20',
        ],

        // Saved item styling (subtle, no purple)
        showSaveIndicator && [
          'border-gray-200',
        ],

        className
      )}
      style={style}
      {...cardProps}
    >
      {/* Image section with overlay */}
      <div
        className={cn(
          "relative",
          // Make image area clickable when card has actions (to avoid nested buttons)
          hasActions && onTap && "cursor-pointer"
        )}
        onClick={hasActions && onTap ? onTap : undefined}
      >
        <PlaceCardImage
          imageUrl={place.imageUrl}
          imageAlt={place.name}
          aspectRatio={imageAspectRatio}
          showSaveIndicator={showSaveIndicator}
          className={isCompact ? 'rounded-t-card' : 'rounded-t-card'}
        />

        <PlaceCardOverlay
          name={place.name}
          rating={place.rating}
          priceLevel={place.priceLevel}
          variant={variant}
        />
      </div>

      {/* Metadata and actions section (only for expanded cards) */}
      {isExpanded && (
        <>
          <PlaceCardMetadata
            category={place.category}
            distance={place.distance}
            savedDate={savedDate}
            savedFrom={savedFrom}
            userNote={userNote}
            variant={variant}
          />

          {hasActions && (
            <PlaceCardActions
              onRemove={onRemove}
              onSchedule={onSchedule}
            />
          )}
        </>
      )}

      {/* Save button (for search results) */}
      {showSaveButton && onSave && (
        <button
          onClick={(e) => {
            e.stopPropagation() // Prevent triggering card tap
            onSave()
          }}
          className={cn(
            "absolute bottom-1 right-2 z-10",
            "w-4 h-4 bg-magazine-paper hover:bg-magazine-cream rounded-full",
            "border border-magazine-pearl flex items-center justify-center",
            "transition-all duration-200 ease-luxury",
            "focus:outline-none focus:ring-2 focus:ring-magazine-ash/20"
          )}
          title={isSaved ? "Saved to trip" : "Save to trip"}
          aria-label={isSaved ? "Saved to trip" : "Save to trip"}
        >
          {isSaved ? (
            <Check className="w-2 h-2 text-magazine-ash" />
          ) : (
            <Plus className="w-2 h-2 text-magazine-ash" />
          )}
        </button>
      )}

      {/* Chevron indicator (future feature) */}
      {showChevron && (
        <div className="absolute top-3 right-3 text-magazine-paper">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' }}
          >
            <path
              d="M6 4L10 8L6 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </CardWrapper>
  )
}
